import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchAccounts,
  fetchThreadDetail,
  sendThreadReply,
  translateDraft,
  translateMessage,
  translateThread,
  updateThreadStatus,
  uploadThreadAttachments,
  attachmentDownloadUrl,
  useThreads,
  type MailAccount,
  type ThreadDetail,
  type UploadedAttachment,
} from "./api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { prettifyApiError } from "@/shared/api";
import type { ReturnRecord } from "@/features/resi/api";
import { prepareResoDraft, type BozzaReso } from "./resoDraft";
import { BozzaStato, useBozzaRisposta } from "./useBozzaRisposta";
import { formatDateTime, stripHtml } from "@/shared/format";
import { useAutoResizeTextarea } from "@/shared/useAutoResizeTextarea";
import { useDebounced } from "@/shared/useDebounced";
import { useIsMobile } from "@/shared/useMediaQuery";
import { usePassoIndietro } from "@/shared/usePassoIndietro";
import { AzioniBarra, TastoBarra } from "@/shared/layout/MobileTopBar";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { ContextPanel } from "./components/ContextPanel";
import { PanelResizer } from "@/shared/ui/PanelResizer";
import {
  MESSAGGI_CONTESTO_IN_COLONNA_PX,
  useLarghezzaElemento,
  useMessaggiPanelWidths,
} from "./components/panelWidths";
import { ContestoOverlay } from "./components/ContestoOverlay";
import {
  BottoneIcona,
  IconaAereo,
  IconaClaude,
  IconaGraffetta,
  IconaMondo,
} from "./components/AzioniComposer";
import { AgentePanel } from "./components/AgentePanel";
import { bozzaPronta } from "./agente";
import { ThreadList } from "./components/ThreadList";
import { ComposerThumb } from "./components/ComposerThumb";
import { ExcludedSendersDialog } from "./components/ExcludedSendersDialog";
import { SpamAvviso } from "./components/SpamAvviso";
import { MessageBubble } from "./components/MessageBubble";
import { SidebarFilter } from "./components/SidebarFilter";
import { raggruppaPerGiorno } from "@/features/ordini/giorniLista";
import { BotPausePanel } from "./BotPausePanel";
import { canaleAccettaAllegati } from "./messageSide";
import { STATUS_FILTERS, defaultDateFrom } from "./vistaStato";
import {
  findGroupContaining,
  groupThreadsByOrder,
  mergeThreadDetails,
  type MergedThreadDetail,
} from "./threadGrouping";

/** Query del deep-link, da location.search (BrowserRouter/web) O dall'hash
 *  dopo il '?' (HashRouter/desktop file://). Su desktop react-router serializza
 *  ?thread=..&q=.. dentro window.location.hash, quindi search resta vuoto. */
function readDeepLinkParams(): URLSearchParams {
  const search = window.location.search;
  if (search.length > 1) return new URLSearchParams(search);
  const hash = window.location.hash;
  const qi = hash.indexOf("?");
  return new URLSearchParams(qi >= 0 ? hash.slice(qi + 1) : "");
}

/** Rimuove SOLO i param del deep-link, preservando pathname e la route
 *  dell'hash (su HashRouter #/messaggi?.. -> #/messaggi, senza perdere la
 *  navigazione). */
function clearDeepLinkParams(): void {
  const hash = window.location.hash;
  const qi = hash.indexOf("?");
  const cleanHash = qi >= 0 ? hash.slice(0, qi) : hash;
  window.history.replaceState(null, "", window.location.pathname + cleanHash);
}

export function MessaggiPage() {
  // Su schermo stretto (portatile) l'ispettore contesto lascia la griglia e
  // diventa un cassetto: e' la colonna che costa di piu' alla chat e l'unica
  // che si consulta a strappi invece che di continuo. Misurato il 2026-08-24:
  // a 1366px la chat aveva 514px, adesso ne ha ~800.
  const grigliaRef = useRef<HTMLElement>(null);
  const larghezzaGriglia = useLarghezzaElemento(grigliaRef);
  const contestoInColonna = larghezzaGriglia >= MESSAGGI_CONTESTO_IN_COLONNA_PX;
  // Larghezze colonne ridimensionabili + persistite (Davide 2026-07-02),
  // ristrette quanto basta perche' alla chat resti spazio leggibile.
  const panels = useMessaggiPanelWidths(contestoInColonna, larghezzaGriglia);
  const [search, setSearch] = useState("");
  // Vista "Stato" (Davide 2026-07-01): default "Da gestire" = i thread da
  // rispondere, ciò che va lavorato. Non nasconde nulla in modo silenzioso: il
  // filtro è OPT-OUT via il banner «Mostra tutti» (filtersActive lo evidenzia).
  // ⚠️ Il 2026-06-29 il default era «mostra tutto» proprio perché un connector
  // che non marcava `needs_reply` fece sparire un messaggio vero; due giorni
  // dopo è tornato a dipendere da quel flag, e il 2026-09-01 è risuccesso su
  // Metro. La difesa non è il default: è che `needs_reply` non menta, e ora si
  // deduce dall'ultimo messaggio (`mail/stato_thread.py`).
  const [view, setView] = useState("da_gestire");
  const [accountFilter, setAccountFilter] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState<string>(defaultDateFrom);
  const [dateTo, setDateTo] = useState<string>("");
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  // Layout mobile (item 15): un pannello alla volta. 'list' = lista thread a
  // tutto schermo, 'chat' = conversazione. Stato ignorato su desktop (usa la
  // griglia a 4 pannelli invariata). Il ContextPanel su mobile è un overlay.
  const isMobile = useIsMobile();
  // La conversazione aperta e' un passo in cronologia: il «indietro» di
  // Android torna alla lista invece di uscire da Messaggi.
  const chatMobile = usePassoIndietro(isMobile, "omChat");
  // Vale sia per il foglio dal basso del telefono sia per il cassetto da
  // destra del portatile: e' lo stesso pannello, in due vestiti.
  const [contestoAperto, setContestoAperto] = useState(false);
  const [filtriAperti, setFiltriAperti] = useState(false);
  const [detail, setDetail] = useState<MergedThreadDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Cambio stato in volo: la select è controllata da ``detail.internal_status``,
  // quindi senza questo flag l'operatore non ha NESSUN segnale che la POST sia
  // partita (il valore mostrato resta il vecchio finché non torna).
  const [statusBusy, setStatusBusy] = useState(false);
  // Contatore per forzare il ricarico del gruppo aperto dopo un invio: il
  // dettaglio tornato da ``sendThreadReply`` e' di UNA conversazione sola.
  const [ricaricaGruppo, setRicaricaGruppo] = useState(0);
  const [excludedDialogAccountId, setExcludedDialogAccountId] = useState<number | null>(null);
  // Reso creato dalla chat → bozza nel composer. Solo il RESO precompila testo +
  // allegato (design v3); la sostituzione non tocca il composer (il tracking al
  // cliente si propone nel dialog «creato ✓»). Lo stato vive qui perché
  // ContextPanel (che crea) e ChatDetail (che possiede il composer) sono sibling.
  // 🔴 La bozza si porta dietro il thread in cui è NATA: cambiando conversazione
  // mentre l'upload dell'etichetta è in volo, ChatDetail si rimonta
  // (key = detail.id) e l'effect riparte con la bozza ancora valorizzata →
  // l'etichetta del cliente A, con nome, indirizzo e telefono in chiaro sulla
  // LDV, finiva caricata sul thread del cliente B.
  const [resoDraft, setResoDraft] = useState<BozzaReso | null>(null);
  const draftSourceThreadId = detail?.id ?? null;
  const handleReturnCreated = useCallback(
    (record: ReturnRecord, ritiro?: BozzaReso["ritiro"]) => {
      if (record.kind === "reso" && draftSourceThreadId !== null) {
        // Il cassetto NON si chiude da solo: la conferma «creato ✓» e il PDF
        // da riscaricare vivono nel pannello, e chiuderlo al primo successo
        // toglierebbe la rete di sicurezza proprio quando l'allegato al
        // messaggio potrebbe ancora fallire. Lo chiude l'utente, con Esc o
        // dallo sfondo.
        setResoDraft({ record, threadId: draftSourceThreadId, ritiro: ritiro ?? null });
      }
    },
    [draftSourceThreadId],
  );
  const clearResoDraft = useCallback(() => setResoDraft(null), []);

  // Deep-link dalla pagina ordine (round 3): /messaggi?thread=ID&q=REF apre
  // subito la conversazione e filtra la lista sul ref. One-shot al mount, via
  // window.location (niente useSearchParams: nessun vincolo di Router nei
  // test); i param vengono consumati con replaceState cosi' i filtri restano
  // poi liberi.
  // 🔴 HashRouter (desktop Electron, file://): i param stanno nell'HASH
  // (#/messaggi?thread=..), non in location.search — leggo entrambi, e la
  // pulizia preserva la route dell'hash (review round3).
  useEffect(() => {
    const params = readDeepLinkParams();
    const threadParam = params.get("thread");
    const qParam = params.get("q");
    if (!threadParam && !qParam) return;
    if (qParam) {
      setSearch(qParam);
      setView("");
      setDateFrom(""); // la conversazione puo' essere piu' vecchia del default
      // accountFilter nasconderebbe il thread bersaglio dalla lista: il
      // deep-link parte da lista pulita.
      setAccountFilter(null);
    }
    if (threadParam) {
      const id = Number(threadParam);
      if (Number.isFinite(id) && id > 0) {
        setActiveId(id);
        if (isMobile) chatMobile.apri({ passo: false });
      }
    }
    clearDeepLinkParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot al mount
  }, []);

  // Ricerca debounced come ClientiPage/FatturazionePage: l'input resta
  // controllato su ``search`` (digitazione fluida), la GET /api/mail/threads
  // parte dopo 300 ms di quiete invece che a ogni tasto premuto.
  const debouncedSearch = useDebounced(search, 300);
  // Cercare è «trovami QUESTA cosa»: se la vista di stato resta attiva, la
  // reference di un ordine già gestito non si trova e sembra non esistere. La
  // ricerca sospende la vista (`view: ""` = nessun predicato di stato) e lo dice.
  const inRicerca = debouncedSearch.trim().length > 0;
  const filters = useMemo(
    () => ({
      q: debouncedSearch,
      view: inRicerca ? "" : view,
      accountId: accountFilter,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [debouncedSearch, inRicerca, view, accountFilter, dateFrom, dateTo],
  );

  const threads = useThreads(filters);

  // Unione delle conversazioni dello stesso ordine (T9). Puramente di
  // PRESENTAZIONE: il DB tiene i thread separati, ed è corretto così — su
  // Mirakl lo stesso cliente ne apre più d'uno sullo stesso ordine e
  // ``external_order_id`` non è (né deve essere) unico.
  const groupedItems = useMemo(() => groupThreadsByOrder(threads.data.items), [threads.data.items]);

  // Gruppo che CONTIENE il thread aperto. Il deep-link ``?thread=ID`` punta a
  // un thread reale che può non essere il rappresentativo: senza questa
  // risoluzione aprirebbe una card che nella lista non esiste più.
  const gruppoAttivo = useMemo(
    () => (activeId === null ? undefined : findGroupContaining(groupedItems, activeId)),
    [activeId, groupedItems],
  );
  // Id da evidenziare nella lista: quella del rappresentativo se il thread
  // aperto è dentro un gruppo.
  const activeCardId = gruppoAttivo?.id ?? activeId;
  // Thread da caricare per la conversazione aperta. Stringa (non array) come
  // dipendenza dell'effetto: un array nuovo a ogni render rilancerebbe la
  // fetch a ogni tick del polling.
  const chiaveGruppoAperto =
    activeId === null ? null : (gruppoAttivo?.groupThreadIds ?? [activeId]).join(",");

  // Un filtro attivo (vista/marketplace) può nascondere messaggi: il banner
  // "Mostra tutti" lo segnala e ``resetFilters`` li ripristina tutti.
  const filtersActive = view !== "" || accountFilter !== null;
  const resetFilters = useCallback(() => {
    setView("");
    setAccountFilter(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchAccounts()
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch(() => {
        // accounts are optional — empty list is fine.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!chiaveGruppoAperto) {
      setDetail(null);
      return;
    }
    const ids = chiaveGruppoAperto.split(",").map(Number);
    let cancelled = false;
    // Un solo thread → una sola GET, come prima. Più thread → si caricano
    // insieme e si fondono in una conversazione sola, in ordine di data.
    void Promise.all(ids.map((id) => fetchThreadDetail(id)))
      .then((dettagli) => {
        if (!cancelled) {
          setDetail(mergeThreadDetails(dettagli));
          setDetailError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // Audit 2026-05-29: errori utente via prettifyApiError (italiano).
          setDetailError(prettifyApiError(err));
          setDetail(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chiaveGruppoAperto, ricaricaGruppo]);

  /** Dopo un'azione che ritorna UNA conversazione: se ne stiamo mostrando più
   *  d'una, ricarica il gruppo invece di collassare la vista sul solo thread
   *  toccato (l'operatore perderebbe i messaggi delle altre). */
  const applicaAggiornamento = useCallback(
    (updated: ThreadDetail) => {
      if ((chiaveGruppoAperto?.split(",").length ?? 1) > 1) {
        setRicaricaGruppo((n) => n + 1);
      } else {
        setDetail(mergeThreadDetails([updated]));
      }
      void threads.refresh();
    },
    // ``threads.refresh`` è stabile (useCallback in useThreads).
    [chiaveGruppoAperto, threads],
  );

  /** Avviso tracking partito dal dialog della sostituzione: il dialog non ha in
   *  mano la conversazione aggiornata, quindi si ricarica il gruppo aperto. */
  const ricaricaChat = useCallback(() => {
    setRicaricaGruppo((n) => n + 1);
    void threads.refresh();
  }, [threads]);

  async function handleStatusChange(status: string) {
    if (!detail) return;
    // Azzerare qui è l'unica via d'uscita da un errore precedente: l'altro
    // ``setDetailError(null)`` sta nell'effect di caricamento, che non si
    // rilancia se non cambia thread — e cambiare thread rimonta ChatDetail
    // buttando la bozza in scrittura.
    setDetailError(null);
    setStatusBusy(true);
    try {
      applicaAggiornamento(await updateThreadStatus(detail.id, status));
    } catch (err) {
      setDetailError(prettifyApiError(err));
    } finally {
      setStatusBusy(false);
    }
  }

  function handleSent(updated: ThreadDetail) {
    applicaAggiornamento(updated);
  }

  function apriChatMobile(id: number) {
    setActiveId(id);
    chatMobile.apri();
  }

  // ── Layout MOBILE (<md): un pannello alla volta ────────────────────────
  // "minimo vitale" (item 15 punto 6): lista thread a tutto schermo → tap →
  // chat a tutto schermo con back '<' + 'Contesto' (ContextPanel in overlay).
  // Riusa gli stessi componenti/handler del desktop; nessuna delle logiche dei
  // pannelli desktop è toccata. I filtri avanzati/PanelResizer restano desktop
  // (mobile parte dalla vista di default "Da gestire"); sync raggiungibile dal
  // top bar della lista.
  if (isMobile) {
    if (chatMobile.aperto && activeId !== null) {
      // A tutto schermo sopra barra e menu: la conversazione ha bisogno di
      // tutta l'altezza per i messaggi e la risposta, e con la tastiera aperta
      // (viewport `resizes-content`) il composer resta appena sopra i tasti.
      const meta = [detail?.customer_name, detail?.source_label, detail?.external_order_id].filter(
        Boolean,
      );
      return (
        <main className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-card pt-[env(safe-area-inset-top)]">
          <div className="flex h-[60px] shrink-0 items-center gap-1 border-b border-line bg-paper pl-1 pr-2">
            <button
              type="button"
              onClick={chatMobile.chiudi}
              aria-label="Torna alla lista"
              className="flex h-11 min-w-[44px] items-center justify-center rounded-full text-ink active:bg-paper-2"
            >
              <svg
                className="h-5 w-5 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-display text-[17px] font-semibold leading-tight tracking-[-0.01em] text-ink">
                {detail?.subject || "Conversazione"}
              </span>
              {meta.length > 0 && (
                <span className="truncate text-[13px] leading-tight text-ink">
                  {meta.join(" · ")}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setContestoAperto(true)}
              className="om-press flex h-10 shrink-0 items-center rounded-full border border-line-2 bg-card px-4 text-[14px] font-semibold text-ink"
            >
              Ordine
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden [&>section]:min-h-0 [&>section]:flex-1">
            <ChatDetail
              key={detail?.id ?? "none"}
              detail={detail}
              error={detailError}
              statusBusy={statusBusy}
              onStatusChange={(status) => void handleStatusChange(status)}
              onSent={handleSent}
              resoDraft={resoDraft}
              onResoDraftConsumed={clearResoDraft}
              compatto
            />
          </div>
          {contestoAperto && (
            <ContestoOverlay variante="basso" onClose={() => setContestoAperto(false)}>
              <ContextPanel
                threadId={activeId}
                onReturnCreated={handleReturnCreated}
                onMessaggioInviato={ricaricaChat}
              />
            </ContestoOverlay>
          )}
        </main>
      );
    }
    return (
      <main className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-card">
        {/* Sincronizza e filtri nella barra in alto: la lista parte subito. */}
        <AzioniBarra>
          <TastoBarra
            label="Sincronizza messaggi"
            busy={threads.isSyncing}
            onClick={() => void threads.syncNow()}
          >
            <svg
              className="h-[22px] w-[22px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 0 0-14.9-3.5M4 4v4h4M4 13a8 8 0 0 0 14.9 3.5M20 20v-4h-4" />
            </svg>
          </TastoBarra>
          <TastoBarra
            label="Filtri: stato, periodo, marketplace"
            badge={filtersActive ? 1 : 0}
            onClick={() => setFiltriAperti(true)}
          >
            <svg
              className="h-[22px] w-[22px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 6h16M7 12h10M10 18h4" />
            </svg>
          </TastoBarra>
        </AzioniBarra>
        <BottomSheet
          open={filtriAperti}
          onClose={() => setFiltriAperti(false)}
          title="Filtri messaggi"
          footer={
            <button
              type="button"
              onClick={() => setFiltriAperti(false)}
              className="om-press h-12 w-full rounded-xl bg-ink text-[15px] font-semibold text-on-ink"
            >
              Vedi {threads.data.items.length}{" "}
              {threads.data.items.length === 1 ? "conversazione" : "conversazioni"}
            </button>
          }
        >
          {/* La stessa colonna filtri del computer, con voci da pollice. */}
          <div className="-mx-5 [&_aside]:border-r-0 [&_aside]:bg-transparent [&_aside_[data-sync]]:hidden [&_aside_.border-b]:px-5 [&_aside_.border-b]:py-4 [&_aside_button]:min-h-[44px] [&_aside_button]:text-[15px] [&_input]:h-11 [&_input]:rounded-xl [&_input]:px-3 [&_input]:text-[15px] [&_input]:text-ink [&_label]:text-[14px] [&_label]:text-ink">
            <Sidebar
              unread={threads.data.unread}
              needsReply={threads.data.needs_reply}
              total={threads.data.total}
              accounts={accounts}
              accountFilter={accountFilter}
              onAccountChange={setAccountFilter}
              view={view}
              onViewChange={setView}
              dateFrom={dateFrom}
              onDateFromChange={setDateFrom}
              dateTo={dateTo}
              onDateToChange={setDateTo}
              onSync={() => void threads.syncNow()}
              isSyncing={threads.isSyncing}
              onOpenExcluded={(id) => {
                setFiltriAperti(false);
                setExcludedDialogAccountId(id);
              }}
            />
          </div>
        </BottomSheet>
        {excludedDialogAccountId !== null && (
          <ExcludedSendersDialog
            accountId={excludedDialogAccountId}
            accountLabel={
              accounts.find((a) => a.id === excludedDialogAccountId)?.label ??
              accounts.find((a) => a.id === excludedDialogAccountId)?.slug ??
              "Account"
            }
            onClose={() => {
              setExcludedDialogAccountId(null);
              void threads.refresh();
            }}
          />
        )}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden [&>section]:min-h-0 [&>section]:flex-1 [&>section]:border-r-0">
          <ThreadList
            items={groupedItems}
            total={threads.data.total}
            mostrati={threads.data.items.length}
            inRicerca={inRicerca}
            isLoading={threads.isLoading}
            error={threads.error}
            info={threads.info}
            search={search}
            onSearchChange={setSearch}
            activeId={activeCardId}
            onSelect={apriChatMobile}
            filtersActive={filtersActive}
            onResetFilters={resetFilters}
          />
        </div>
      </main>
    );
  }

  return (
    <main
      ref={grigliaRef}
      className="grid h-full min-h-0 flex-1 overflow-hidden bg-paper"
      style={{ gridTemplateColumns: panels.gridTemplateColumns, minHeight: 0 }}
    >
      <Sidebar
        unread={threads.data.unread}
        needsReply={threads.data.needs_reply}
        total={threads.data.total}
        accounts={accounts}
        accountFilter={accountFilter}
        onAccountChange={setAccountFilter}
        view={view}
        onViewChange={setView}
        dateFrom={dateFrom}
        onDateFromChange={setDateFrom}
        dateTo={dateTo}
        onDateToChange={setDateTo}
        onSync={() => void threads.syncNow()}
        isSyncing={threads.isSyncing}
        onOpenExcluded={setExcludedDialogAccountId}
      />
      {excludedDialogAccountId !== null && (
        <ExcludedSendersDialog
          accountId={excludedDialogAccountId}
          accountLabel={
            accounts.find((a) => a.id === excludedDialogAccountId)?.label ??
            accounts.find((a) => a.id === excludedDialogAccountId)?.slug ??
            "Account"
          }
          onClose={() => {
            setExcludedDialogAccountId(null);
            // Refresh list-threads quando si chiude (potrebbero essere
            // stati aggiunti/rimossi pattern, list-threads filtra server-side).
            void threads.refresh();
          }}
        />
      )}
      <PanelResizer
        label="Ridimensiona filtri"
        value={panels.widths.sidebar}
        direction={1}
        onChange={(px) => panels.setWidth("sidebar", px)}
        onReset={() => panels.resetWidth("sidebar")}
      />
      <ThreadList
        items={groupedItems}
        total={threads.data.total}
        mostrati={threads.data.items.length}
        inRicerca={inRicerca}
        isLoading={threads.isLoading}
        error={threads.error}
        info={threads.info}
        search={search}
        onSearchChange={setSearch}
        activeId={activeCardId}
        onSelect={setActiveId}
        filtersActive={filtersActive}
        onResetFilters={resetFilters}
      />
      <PanelResizer
        label="Ridimensiona lista messaggi"
        value={panels.widths.threads}
        direction={1}
        onChange={(px) => panels.setWidth("threads", px)}
        onReset={() => panels.resetWidth("threads")}
      />
      <ChatDetail
        // Audit 2026-06-24 (FIX 1): montiamo ChatDetail per-thread con una
        // ``key`` legata all'id. Al cambio thread React smonta/rimonta il
        // componente, azzerando per COSTRUZIONE tutto lo stato locale del
        // composer (``body``, ``attachments``, ``composerError``,
        // ``lastDraftTargetLang``, cache traduzioni). Senza, la bozza/gli
        // allegati del thread A restavano e finivano inviati a B — con
        // allegati → NotFoundError perche' la dir uploads e' per-thread.
        // La ``key`` e' la soluzione piu' robusta del reset manuale: nessun
        // nuovo stato del composer puo' "sfuggire" al reset in futuro.
        key={detail?.id ?? "none"}
        detail={detail}
        error={detailError}
        statusBusy={statusBusy}
        onStatusChange={(status) => void handleStatusChange(status)}
        onSent={handleSent}
        resoDraft={resoDraft}
        onResoDraftConsumed={clearResoDraft}
        onApriContesto={contestoInColonna ? undefined : () => setContestoAperto(true)}
      />
      {contestoInColonna && (
        <>
          <PanelResizer
            label="Ridimensiona contesto ordine"
            value={panels.widths.context}
            direction={-1}
            onChange={(px) => panels.setWidth("context", px)}
            onReset={() => panels.resetWidth("context")}
          />
          <ContextPanel
            threadId={activeId}
            onReturnCreated={handleReturnCreated}
            onMessaggioInviato={ricaricaChat}
          />
        </>
      )}
      {!contestoInColonna && contestoAperto && (
        <ContestoOverlay variante="destra" onClose={() => setContestoAperto(false)}>
          <ContextPanel
            threadId={activeId}
            onReturnCreated={handleReturnCreated}
            onMessaggioInviato={ricaricaChat}
          />
        </ContestoOverlay>
      )}
    </main>
  );
}

// 4 colonne (filtri, lista thread, chat, ispettore) + 3 divisori trascinabili
// sopra i 1400px; sotto, l'ispettore esce dalla griglia e diventa un cassetto
// (3 colonne + 2 divisori). Larghezze gestite da useMessaggiPanelWidths
// (default in MESSAGGI_PANEL_DEFAULTS, persistite in localStorage — Davide
// 2026-07-02), ristrette a runtime quando lo schermo non le regge.

function Sidebar({
  unread,
  needsReply,
  total,
  accounts,
  accountFilter,
  onAccountChange,
  view,
  onViewChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  onSync,
  isSyncing,
  onOpenExcluded,
}: {
  unread: number;
  needsReply: number;
  total: number;
  accounts: MailAccount[];
  accountFilter: number | null;
  onAccountChange: (id: number | null) => void;
  view: string;
  onViewChange: (s: string) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  onSync: () => void;
  isSyncing: boolean;
  onOpenExcluded: (accountId: number) => void;
}) {
  return (
    <aside className="flex min-h-0 flex-col overflow-y-auto border-r border-line bg-card scroll-paper">
      <div className="border-b border-line p-3.5">
        <button
          type="button"
          data-sync
          onClick={onSync}
          disabled={isSyncing}
          aria-busy={isSyncing}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-full border border-violet bg-violet px-4 text-[13px] font-semibold text-white transition-colors hover:border-violet-2 hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-55"
        >
          <svg
            className={`h-4 w-4 fill-none stroke-current stroke-[1.9] [stroke-linecap:round] [stroke-linejoin:round] ${isSyncing ? "animate-spin" : ""}`}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M20 11a8 8 0 0 0-14.9-3.5M4 4v4h4M4 13a8 8 0 0 0 14.9 3.5M20 20v-4h-4" />
          </svg>
          {/* «Sincronizza» e basta: sta su una riga anche nella colonna stretta. */}
          {isSyncing ? "Sincronizzo…" : "Sincronizza"}
        </button>
        <p className="mt-2 text-center text-[12px] tabular-nums text-ink">
          <b className="font-bold">{total}</b> totali · <b className="font-bold">{unread}</b> non
          letti
        </p>
      </div>

      <div className="border-b border-line p-3.5">
        <SidebarEyebrow>Stato</SidebarEyebrow>
        <div className="space-y-1">
          {STATUS_FILTERS.map((option) => (
            <SidebarFilter
              key={option.value || "all"}
              active={view === option.value}
              onClick={() => onViewChange(option.value)}
              label={option.label}
              tono={option.tono}
              count={option.value === "da_gestire" ? needsReply || undefined : undefined}
            />
          ))}
        </div>
      </div>

      <div className="border-b border-line p-3.5">
        <SidebarEyebrow>Periodo</SidebarEyebrow>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-[12.5px] text-ink">
            <span className="w-6 shrink-0 font-semibold">Dal</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => onDateFromChange(event.target.value)}
              className="h-9 min-w-0 flex-1 rounded-full border border-line-2 bg-card px-3 text-[12.5px] tabular-nums text-ink outline-none transition-colors focus:border-ink"
            />
          </label>
          <label className="flex items-center gap-2 text-[12.5px] text-ink">
            <span className="w-6 shrink-0 font-semibold">Al</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => onDateToChange(event.target.value)}
              className="h-9 min-w-0 flex-1 rounded-full border border-line-2 bg-card px-3 text-[12.5px] tabular-nums text-ink outline-none transition-colors focus:border-ink"
            />
          </label>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => {
                onDateFromChange("");
                onDateToChange("");
              }}
              className="rounded-full px-2 py-0.5 text-[12px] font-semibold text-ink underline-offset-4 hover:bg-paper-2 hover:underline"
            >
              Cancella periodo
            </button>
          )}
        </div>
      </div>

      <div className="border-b border-line p-3.5">
        <SidebarEyebrow>Marketplace</SidebarEyebrow>
        <SidebarFilter
          active={accountFilter === null}
          onClick={() => onAccountChange(null)}
          label="Tutti"
          tono="annullato"
        />
        {accounts.map((account) => {
          const isActive = accountFilter === account.id;
          return (
            <div key={account.id} className="group mt-1 flex items-stretch gap-0.5">
              <button
                type="button"
                onClick={() => onAccountChange(account.id)}
                aria-pressed={isActive}
                className={[
                  "flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full border px-3 text-left text-[12.5px] font-semibold text-ink transition-colors",
                  isActive
                    ? "border-ink bg-ink text-on-ink"
                    : "border-transparent hover:border-line-2 hover:bg-paper",
                ].join(" ")}
              >
                {/* Logo a larghezza fissa per allineare i nomi a colonna,
                    a prescindere dalla forma del badge. */}
                <span className="flex w-4 shrink-0 justify-center">
                  <MarketplaceLogo
                    source={account.source}
                    size={16}
                    label={account.label || undefined}
                  />
                </span>
                <span className="min-w-0 flex-1 truncate">{account.label || account.slug}</span>
                {account.enabled ? null : (
                  <span
                    className={`shrink-0 rounded-full px-1.5 text-[11px] font-semibold ${isActive ? "bg-white/20" : "bg-paper-2"}`}
                  >
                    spento
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => onOpenExcluded(account.id)}
                title="Gestisci mittenti esclusi per questo account"
                aria-label={`Mittenti esclusi ${account.label || account.slug}`}
                // Largo zero finche' non serve: da spento non ruba spazio al
                // nome del marketplace (a 190px «Leroy Merlin» si tagliava).
                className="grid w-0 shrink-0 place-items-center overflow-hidden rounded-full text-ink opacity-0 transition-[width,opacity] hover:bg-paper-2 focus-visible:w-8 focus-visible:opacity-100 group-hover:w-8 group-hover:opacity-100 max-md:w-11 max-md:opacity-100"
              >
                <svg
                  className="h-4 w-4 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M5.6 5.6L18.4 18.4" />
                </svg>
              </button>
            </div>
          );
        })}
        {accounts.length === 0 && (
          <p className="mt-1 text-[12px] text-ink">
            Nessun account configurato. Aggiungi un account Mirakl in Impostazioni.
          </p>
        )}
      </div>
    </aside>
  );
}

/** Titolo di sezione della sidebar. Redesign 2026-10-27: normale, nero e in
 *  grassetto (era un maiuscoletto grigio da 9,5px, faticoso da leggere). */
function SidebarEyebrow({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-[13px] font-bold text-ink">{children}</div>;
}

function ChatDetail({
  detail,
  error,
  statusBusy = false,
  onStatusChange,
  onSent,
  resoDraft,
  onResoDraftConsumed,
  onApriContesto,
  compatto = false,
}: {
  detail: MergedThreadDetail | null;
  error: string | null;
  /** Cambio stato in volo: spegne la select finché la POST non torna. */
  statusBusy?: boolean;
  onStatusChange: (status: string) => void;
  onSent: (detail: ThreadDetail) => void;
  /** Reso creato dalla chat + thread in cui è nato: precompila il composer
   *  (allegato etichetta + testo template) SOLO se combacia con la
   *  conversazione aperta. ``null`` = nessuna bozza pendente. */
  resoDraft?: BozzaReso | null;
  /** Chiamato quando la bozza è stata consumata (successo o errore), per
   *  azzerare ``resoDraft`` lato MessaggiPage ed evitare ri-esecuzioni. */
  onResoDraftConsumed?: () => void;
  /** Presente solo quando l'ispettore contesto NON ha una colonna sua
   *  (portatile): apre il cassetto. ``undefined`` = la colonna c'è, il
   *  bottone non serve e non compare. */
  onApriContesto?: () => void;
  /** Telefono: oggetto, cliente e ordine li dice gia' la barra della
   *  conversazione, qui restano solo i comandi (stato, traduzione). */
  compatto?: boolean;
}) {
  const [body, setBody] = useState("");
  // Il composer si adatta in altezza al contenuto (cresce/diminuisce, poi
  // scrolla). Triggera su `body` -> copre anche il testo messo dal tasto traduci.
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useAutoResizeTextarea(composerRef, body);
  // Item 16: destinatario della risposta, come nella messaggistica nativa
  // Mirakl. Default "customer" = si risponde al CLIENTE (prima andava sempre
  // a cliente + operatore insieme). Gli altri canali hanno un solo
  // interlocutore, quindi il selettore compare solo per Mirakl.
  const [recipient, setRecipient] = useState<"customer" | "operator" | "all">("customer");
  const [isSending, setIsSending] = useState(false);
  // Audit 2026-05-29: flag dedicato all'upload allegati. Prima uploadFiles
  // riusava ``isSending`` → il bottone Invia mostrava "Invio…" durante il
  // caricamento di un file (feedback fuorviante, viola "feedback legato
  // all'azione").
  const [isUploading, setIsUploading] = useState(false);
  // Bottone "Traduci la bozza nella lingua del cliente" (audit 2026-05-20): traduce
  // la bozza nella lingua dei messaggi inbound del thread. Il backend
  // auto-rileva la lingua. ``lastDraftTargetLang`` mostra all'operatore
  // in che lingua e' stato tradotto, cosi' sa di non aver cliccato per
  // sbaglio (es. "tradotto in: en").
  const [isTranslatingDraft, setIsTranslatingDraft] = useState(false);
  const [lastDraftTargetLang, setLastDraftTargetLang] = useState<string | null>(null);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  // Agente Claude (30/10/2026): pannello aperto a richiesta, si chiude al cambio chat.
  const [agenteAperto, setAgenteAperto] = useState(false);
  // Bozza preparata da Claude all'arrivo del messaggio: qui solo l'avviso, il
  // testo lo mostra il pannello. Non entra mai da sola nella casella.
  const [prontaThreadId, setProntaThreadId] = useState<number | null>(null);
  const statoBozza = useBozzaRisposta(detail?.id ?? null, {
    body,
    attachments,
    recipient,
    setBody,
    setAttachments,
    setRecipient,
  });

  // Replica legacy ``tab_messaggi.py::_translations``: cache locale
  // ``message_id -> testo tradotto`` + flag ``visible`` per toggle. La
  // cache vive nella vita della selezione thread (cleared al cambio).
  const [translations, setTranslations] = useState<Record<number, string>>({});
  const [translationsVisible, setTranslationsVisible] = useState<Record<number, boolean>>({});
  const [translatingIds, setTranslatingIds] = useState<Set<number>>(new Set());
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);
  const [translateError, setTranslateError] = useState<string | null>(null);
  // Audit 2026-05-29: id del thread attivo, letto al RITORNO delle async di
  // traduzione. Una promise del thread A che risolve dopo lo switch a B non
  // deve scrivere nella cache (le chiavi sono message_id auto-increment:
  // collisione cross-thread improbabile ma possibile → traduzione errata).
  const activeThreadIdRef = useRef<number | null>(detail?.id ?? null);

  // Cambio thread = reset cache traduzioni (memoria-bound, evita perdite
  // su navigazione lunga). Replica behavior legacy.
  useEffect(() => {
    activeThreadIdRef.current = detail?.id ?? null;
    setTranslations({});
    setTranslationsVisible({});
    setTranslatingIds(new Set());
    setIsTranslatingAll(false);
    setTranslateError(null);
    setLastDraftTargetLang(null);
    setAgenteAperto(false);
  }, [detail?.id]);

  useEffect(() => {
    const threadId = detail?.id;
    setProntaThreadId(null);
    if (threadId == null) return;
    let vivo = true;
    bozzaPronta(threadId)
      .then((b) => vivo && b && setProntaThreadId(threadId))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [detail?.id]);

  // Bozza reso da chat: scarica l'etichetta, la allega al thread e SOLO dopo
  // l'upload riuscito precompila il testo template (ordine voluto: non promettere
  // un allegato assente). Su errore mostriamo il messaggio; il PDF resta comunque
  // scaricabile dallo stato «creato ✓» del dialog. Invio sempre manuale.
  const draftThreadId = detail?.id ?? null;
  const draftSource = detail?.source;
  useEffect(() => {
    if (!resoDraft || draftThreadId == null) return;
    if (resoDraft.threadId !== draftThreadId) {
      // Bozza nata in un'ALTRA conversazione: si scarta senza caricare nulla.
      // Allegarla qui manderebbe l'etichetta del cliente A — dati personali in
      // chiaro — sul thread del cliente B, e l'upload è lato SERVER.
      onResoDraftConsumed?.();
      return;
    }
    let cancelled = false;
    setIsUploading(true);
    setComposerError(null);
    void prepareResoDraft(draftThreadId, resoDraft.record, resoDraft.ritiro, draftSource)
      .then(({ attachments: uploaded, message }) => {
        if (cancelled) return;
        setAttachments((prev) => [...prev, ...uploaded]);
        setBody(message);
      })
      .catch((err: unknown) => {
        if (!cancelled) setComposerError(prettifyApiError(err));
      })
      .finally(() => {
        if (cancelled) return;
        setIsUploading(false);
        onResoDraftConsumed?.();
      });
    return () => {
      cancelled = true;
    };
  }, [resoDraft, draftThreadId, draftSource, onResoDraftConsumed]);

  async function submitReply() {
    if (!detail || !body.trim()) return;
    setIsSending(true);
    try {
      const response = await sendThreadReply(
        detail.id,
        body.trim(),
        attachments.map((attachment) => attachment.id),
        recipient,
      );
      setBody("");
      setAttachments([]);
      setComposerError(null);
      onSent(response.thread);
    } catch (err) {
      setComposerError(prettifyApiError(err));
    } finally {
      setIsSending(false);
    }
  }

  async function uploadFiles(files: FileList | null) {
    if (!detail || !files || files.length === 0) return;
    setIsUploading(true);
    try {
      const uploaded = await uploadThreadAttachments(detail.id, Array.from(files));
      setAttachments((prev) => [...prev, ...uploaded]);
      setComposerError(null);
    } catch (err) {
      setComposerError(prettifyApiError(err));
    } finally {
      setIsUploading(false);
    }
  }

  // Toggle traduzione di un singolo messaggio. Audit 2026-05-28: prima
  // cerca la cache locale (ottimistico, no toggle reset al cambio
  // thread), poi quella server (``message.body_translated_it``
  // dalla migration 0025). Solo se entrambe vuote chiama l'API.
  // Replica + estende ``_do_translate_msg`` legacy.
  async function toggleTranslateMessage(
    messageId: number,
    sourceText: string,
    cachedFromServer: string | null,
  ) {
    setTranslateError(null);
    // Audit 2026-05-29: id catturato a inizio richiesta; al ritorno scartiamo
    // i risultati se il thread attivo e' cambiato (evita contaminazione cache).
    const requestedThreadId = detail?.id ?? null;
    // Cache locale: gia' tradotto in questa sessione → toggle visibility.
    if (translations[messageId] !== undefined) {
      setTranslationsVisible((prev) => ({
        ...prev,
        [messageId]: !prev[messageId],
      }));
      return;
    }
    // Cache server (persistente): salviamo nella cache locale + mostra.
    // Nessuna chiamata API → toggle istantaneo. Davide vede la
    // traduzione subito senza spinner.
    if (cachedFromServer !== null) {
      setTranslations((prev) => ({ ...prev, [messageId]: cachedFromServer }));
      setTranslationsVisible((prev) => ({ ...prev, [messageId]: true }));
      return;
    }
    if (!sourceText.trim()) return;
    setTranslatingIds((prev) => {
      const next = new Set(prev);
      next.add(messageId);
      return next;
    });
    try {
      const response = await translateMessage(messageId, "italiano");
      // Thread cambiato durante la richiesta: scarta, non contaminare B.
      if (activeThreadIdRef.current !== requestedThreadId) return;
      setTranslations((prev) => ({ ...prev, [messageId]: response.translated_text }));
      setTranslationsVisible((prev) => ({ ...prev, [messageId]: true }));
    } catch (err) {
      if (activeThreadIdRef.current !== requestedThreadId) return;
      setTranslateError(prettifyApiError(err));
    } finally {
      // Cleanup sempre: rimuovi il messageId dal set "in traduzione" anche se
      // il thread e' cambiato (lo spinner non deve restare appeso). La guardia
      // sul thread vale solo per try/catch (non scrivere stato del thread
      // sbagliato), non per il cleanup. (audit 2026-05-29)
      setTranslatingIds((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  }

  // Replica legacy ``_translate_all_btn``: traduci TUTTI i messaggi del
  // thread in italiano. Batch backend gestisce il rate limit (5/min).
  async function translateAll() {
    if (!detail) return;
    // Audit 2026-05-29: stessa guardia di toggleTranslateMessage — scarta i
    // risultati se l'utente cambia thread mentre la batch e' in volo.
    const requestedThreadId = detail.id;
    setIsTranslatingAll(true);
    setTranslateError(null);
    try {
      const response = await translateThread(detail.id, "italiano");
      if (activeThreadIdRef.current !== requestedThreadId) return;
      const map: Record<number, string> = {};
      const visible: Record<number, boolean> = {};
      response.items.forEach((item) => {
        map[item.message_id] = item.translated_text;
        visible[item.message_id] = true;
      });
      setTranslations((prev) => ({ ...prev, ...map }));
      setTranslationsVisible((prev) => ({ ...prev, ...visible }));
      const errKeys = Object.keys(response.errors);
      if (errKeys.length > 0) {
        setTranslateError(
          `Traduzione completata con ${errKeys.length} errori. Riprova sui messaggi falliti.`,
        );
      }
    } catch (err) {
      if (activeThreadIdRef.current !== requestedThreadId) return;
      setTranslateError(prettifyApiError(err));
    } finally {
      // Reset solo se siamo ancora sullo stesso thread; in caso di switch il
      // useEffect [detail?.id] ha gia' rimesso isTranslatingAll a false.
      if (activeThreadIdRef.current === requestedThreadId) setIsTranslatingAll(false);
    }
  }

  /** Traduce la bozza nel composer nella lingua del cliente.
   *  Backend auto-rileva la lingua dai messaggi inbound del thread se
   *  ``targetLang`` non specificato. Audit 2026-05-20 (Davide). */
  async function translateDraftForCustomer() {
    if (!detail) return;
    const draft = body.trim();
    if (!draft) {
      setComposerError("Niente da tradurre: digita prima una bozza.");
      return;
    }
    setIsTranslatingDraft(true);
    setComposerError(null);
    try {
      const response = await translateDraft(detail.id, draft);
      setBody(response.translated_text);
      setLastDraftTargetLang(response.target_lang);
    } catch (err) {
      setComposerError(prettifyApiError(err));
    } finally {
      setIsTranslatingDraft(false);
    }
  }

  // 🔴 Solo se la conversazione NON c'è: ``error`` porta anche i fallimenti del
  // cambio stato, e sostituire la chat con un banner butterebbe via messaggi,
  // allegati caricati e la bozza già digitata per un blip di rete su un'altra
  // azione. Con la chat presente l'errore va nel banner in testata.
  if (error && !detail) {
    return (
      <section className="flex min-h-0 items-center justify-center bg-paper">
        <ErrorBanner message={error} />
      </section>
    );
  }
  if (!detail) {
    return (
      <section className="flex min-h-0 flex-col items-center justify-center bg-paper px-6 text-center">
        <svg
          className="mb-3 h-9 w-9 fill-none stroke-current stroke-[1.5] text-ink [stroke-linecap:round] [stroke-linejoin:round]"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-5.1A8 8 0 1 1 21 12z" />
        </svg>
        <p className="font-display text-[18px] font-bold text-ink">Seleziona una conversazione</p>
        <p className="mt-1 max-w-[360px] text-[13px] text-ink">
          Scegli un cliente dalla lista: qui leggi i messaggi, rispondi e alleghi file.
        </p>
      </section>
    );
  }
  // Primo messaggio di ogni giorno → titolo del giorno («Oggi», «Ieri», «sabato
  // 25 ottobre»), con la stessa regola della lista ordini.
  const inizioGiorno = new Map(
    raggruppaPerGiorno(detail.messages, (m) => m.created_at_remote).map((g) => [
      g.ordini[0]?.id,
      g.sottotitolo ? `${g.titolo} · ${g.sottotitolo}` : g.titolo,
    ]),
  );
  return (
    <section className="flex min-h-0 flex-col bg-card">
      <header className="flex-shrink-0 border-b border-line bg-card px-5 pb-3.5 pt-4 max-md:px-3 max-md:py-2.5">
        <div
          className={`mb-2 flex items-center gap-2 ${compatto ? "mb-0 gap-2 [&>button]:h-10 [&>button]:rounded-full [&>button]:px-4 [&>button]:text-[14px] [&>button]:text-ink [&>select]:h-10 [&>select]:min-w-0 [&>select]:flex-1 [&>select]:rounded-full [&>select]:px-3 [&>select]:text-[15px] [&>select]:text-ink" : ""}`}
        >
          {compatto ? null : (
            <h2 className="min-w-0 flex-1 truncate font-display text-[20px] font-bold tracking-[-0.01em] text-ink">
              {detail.subject || "(senza oggetto)"}
            </h2>
          )}
          {onApriContesto && (
            <button
              type="button"
              onClick={onApriContesto}
              className="h-[34px] shrink-0 rounded-full border border-line-2 bg-card px-3.5 text-[12.5px] font-semibold text-ink transition-colors hover:border-ink"
            >
              Contesto
            </button>
          )}
          <button
            type="button"
            onClick={() => void translateAll()}
            disabled={isTranslatingAll || detail.messages.length === 0}
            title="Traduci tutti i messaggi del thread in italiano (legacy parity)"
            className="h-[34px] shrink-0 rounded-full border border-line-2 bg-card px-3.5 text-[12.5px] font-semibold text-ink transition-colors hover:border-ink disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isTranslatingAll ? "Traduco…" : "Traduci tutto"}
          </button>
          <select
            value={detail.internal_status}
            aria-label="Stato della conversazione"
            disabled={statusBusy}
            aria-busy={statusBusy}
            onChange={(event) => onStatusChange(event.target.value)}
            className="h-[34px] shrink-0 rounded-full border border-ink bg-card pl-3.5 pr-8 text-[12.5px] font-semibold text-ink disabled:opacity-50"
          >
            <option value="da_gestire">Da gestire</option>
            <option value="in_corso">In corso</option>
            <option value="chiuso">Chiuso</option>
            {detail.internal_status === "spam" && (
              <option value="spam" disabled>
                Spam
              </option>
            )}
          </select>
        </div>
        <div
          className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-ink ${compatto ? "hidden" : ""}`}
        >
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <MarketplaceLogo
              source={detail.source}
              size={16}
              label={detail.source_label || undefined}
            />
            {detail.source_label}
          </span>
          <span className="font-bold">{detail.customer_name || "Cliente"}</span>
          {detail.customer_org_name && detail.customer_org_name !== detail.customer_name && (
            <span>{detail.customer_org_name}</span>
          )}
          {detail.external_order_id && (
            <span>
              Ordine <b className="font-bold tabular-nums">{detail.external_order_id}</b>
            </span>
          )}
          <span className="tabular-nums">
            {detail.message_count} {detail.message_count === 1 ? "messaggio" : "messaggi"}
          </span>
        </div>
        <BotPausePanel threadId={detail.id} source={detail.source} />
        {/* Errore di AZIONE (cambio stato) con la chat a schermo: banner, non
            più schermata piena. Vedi la guardia ``error && !detail`` sopra. */}
        <ErrorBanner message={error} className="mt-2" />
        {detail.internal_status === "spam" && (
          <SpamAvviso
            motivi={detail.spam_motivi ?? []}
            busy={statusBusy}
            onNonSpam={() => onStatusChange("da_gestire")}
          />
        )}
        {translateError && (
          <div className="mt-2 rounded-xl border border-amber/30 bg-amber-soft px-3 py-2 text-[12px] text-ink">
            {translateError}
          </div>
        )}
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto bg-paper px-5 py-5 scroll-paper max-md:px-3 max-md:py-4">
        {detail.messages.map((message, indice) => {
          // Separatore al cambio di conversazione d'origine. Senza, i messaggi
          // di due thread diversi sembrerebbero un filo unico e l'operatore
          // non capirebbe perché il cliente "ricomincia da capo".
          const precedente = (indice > 0 ? detail.messages[indice - 1] : null) ?? null;
          const nuovaConversazione =
            precedente !== null && precedente.threadId !== message.threadId;
          const giorno = inizioGiorno.get(message.id);
          return (
            <Fragment key={message.id}>
              {/* Redesign 2026-10-27: i messaggi divisi per giorno, come gli
                  ordini; la data piena resta sul singolo messaggio. */}
              {giorno && (
                <div className="flex justify-center pt-1" data-testid="giorno-chat">
                  <span className="rounded-full border border-line-2 bg-card px-3 py-0.5 text-[12px] font-semibold text-ink">
                    {giorno}
                  </span>
                </div>
              )}
              {nuovaConversazione && (
                <div className="flex items-center gap-2 py-1" data-testid="nuova-conversazione">
                  <span className="h-px flex-1 bg-line-2" />
                  <span className="shrink-0 text-[12px] font-semibold text-ink">
                    Nuova conversazione aperta il {formatDateTime(message.created_at_remote)}
                  </span>
                  <span className="h-px flex-1 bg-line-2" />
                </div>
              )}
              <MessageBubble
                message={message}
                translatedText={translations[message.id]}
                translationVisible={translationsVisible[message.id] === true}
                isTranslating={translatingIds.has(message.id)}
                onToggleTranslate={() =>
                  void toggleTranslateMessage(
                    message.id,
                    message.body_plain || stripHtml(message.body_html),
                    // Cache server: ``body_translated_it`` puo' essere null
                    // (mai tradotto), stringa vuota (cache vuota per body
                    // vuoto) o testo (cached). La stringa vuota e' un'OK
                    // marker → toggle apre comunque ma mostra "" → niente
                    // chiamata API runtime.
                    message.body_translated_it,
                  )
                }
              />
            </Fragment>
          );
        })}
        {detail.messages.length === 0 && (
          <p className="text-center text-[13px] text-ink">Nessun messaggio nel thread.</p>
        )}
      </div>

      {/* Temu non accetta risposte via API: si scrive dal Seller Center. */}
      {detail.source === "temu" ? (
        <footer className="flex-shrink-0 border-t border-line bg-card px-5 py-3 text-[13px] font-semibold text-ink max-md:px-3 max-md:pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
          Su Temu si risponde dal Seller Center
        </footer>
      ) : (
        <footer className="flex-shrink-0 border-t border-line bg-card px-5 py-3 max-md:px-3 max-md:pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
          <ErrorBanner message={composerError} className="mb-2" />
          {/* Con più conversazioni fuse l'operatore deve sapere DOVE finisce la
            sua risposta: in quella che aspetta risposta, se no la più recente
            (``destinatario`` in threadGrouping). ``detail.id`` è la sua id REALE. */}
          {detail.groupThreadIds.length > 1 && (
            <p className="mb-2 text-[12px] text-ink" data-testid="composer-target-hint">
              La risposta va in «{detail.subject}», fra le {detail.groupThreadIds.length}{" "}
              conversazioni unite qui.
            </p>
          )}
          {!agenteAperto && prontaThreadId === detail.id && (
            <button
              type="button"
              onClick={() => setAgenteAperto(true)}
              data-testid="avviso-bozza-pronta"
              className="mb-2 w-full rounded-2xl border border-line-2 bg-paper px-3.5 py-2 text-left text-[12.5px] font-semibold text-ink hover:bg-card"
            >
              Claude ha già preparato una bozza. Aprila
            </button>
          )}
          {agenteAperto && (
            <AgentePanel
              key={detail.id}
              threadId={detail.id}
              testoCasella={body}
              onBozza={(testo) => {
                setBody(testo);
                setLastDraftTargetLang(null);
              }}
              onChiudi={() => setAgenteAperto(false)}
            />
          )}
          <textarea
            ref={composerRef}
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              // Audit 2026-05-20: badge "tradotto in: X" diventa stale
              // appena l'operatore modifica il testo, quindi lo puliamo.
              if (lastDraftTargetLang !== null) setLastDraftTargetLang(null);
            }}
            rows={4}
            placeholder="Scrivi una risposta..."
            // Auto-resize via useAutoResizeTextarea: l'altezza segue il contenuto,
            // quindi niente maniglia manuale (resize-none) per non confliggere.
            className="min-h-[92px] max-h-[360px] w-full resize-none rounded-2xl border border-line-2 bg-paper px-3.5 py-2.5 text-[13.5px] text-ink outline-none transition-[border-color,background-color,box-shadow] placeholder:text-ink-3 focus:border-ink focus:bg-card focus:shadow-[0_0_0_3px_rgba(31,27,22,0.08)]"
          />
          {attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {attachments.map((attachment) => {
                const isImage = (attachment.content_type || "").toLowerCase().startsWith("image/");
                return (
                  <span
                    key={attachment.id}
                    className="inline-flex items-center gap-2 rounded-full border border-line-2 bg-card py-1 pl-1 pr-2 text-[12px] text-ink"
                  >
                    {/* Audit 2026-05-28 (Davide): preview thumbnail per gli
                      allegati immagine PRIMA dell'invio. L'operatore vede
                      a colpo d'occhio cosa sta per spedire al cliente
                      (utile quando si caricano piu' foto di una scelta
                      o varianti). */}
                    {isImage ? (
                      <ComposerThumb
                        src={attachmentDownloadUrl(attachment.id)}
                        fileName={attachment.file_name}
                      />
                    ) : null}
                    <span className="max-w-[200px] truncate" title={attachment.file_name}>
                      {attachment.file_name}
                    </span>
                    <button
                      type="button"
                      aria-label={`Rimuovi ${attachment.file_name}`}
                      onClick={() =>
                        setAttachments((prev) => prev.filter((item) => item.id !== attachment.id))
                      }
                      className="grid h-6 w-6 place-items-center rounded-full text-ink hover:bg-red-soft"
                    >
                      <svg
                        className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.9] [stroke-linecap:round]"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          {/* `flex-wrap` su entrambi i livelli e `shrink-0` su Invia: e' la
            garanzia strutturale che il tasto per inviare non possa MAI
            finire fuori dalla colonna, qualunque larghezza abbia la chat.
            Prima era un flex rigido: a 588px di colonna la barra ne chiedeva
            600 e Invia usciva di 32px (misurato il 2026-08-25). */}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {/* Audit 2026-05-29: feedback dell'upload sul bottone Allega
                (``isUploading``), non su Invia. */}
              {/* Resta una <label> (contiene l'input file nascosto), ma con
                l'aspetto e l'ingombro dei tasti-icona accanto. Il nome lo porta
                `aria-label`; `title` lo mostra al mouse. */}
              <label
                aria-label={isUploading ? "Carico l'allegato…" : "Allega file"}
                title={isUploading ? "Carico l'allegato…" : "Allega file"}
                className={`${canaleAccettaAllegati(detail.source) ? "flex" : "hidden"} h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line-2 bg-card text-ink transition-colors hover:border-ink ${
                  isUploading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                }`}
              >
                {isUploading ? (
                  <svg
                    className="h-4 w-4 animate-spin fill-none stroke-current stroke-[1.8] [stroke-linecap:round]"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M21 12a9 9 0 1 1-6.2-8.6" />
                  </svg>
                ) : (
                  <IconaGraffetta />
                )}
                <input
                  type="file"
                  multiple
                  disabled={isUploading}
                  className="hidden"
                  onChange={(event) => {
                    void uploadFiles(event.target.files);
                    event.target.value = "";
                  }}
                />
              </label>
              {/* Destinatario (item 16): solo Mirakl distingue cliente/operatore.
                Primo della fila perche' e' la scelta piu' consequenziale. */}
              {detail.source === "mirakl" && (
                <select
                  value={recipient}
                  onChange={(event) =>
                    setRecipient(event.target.value as "customer" | "operator" | "all")
                  }
                  aria-label="Destinatario della risposta"
                  title="A chi va la risposta, come nella messaggistica Mirakl"
                  className="h-9 rounded-full border border-line-2 bg-card pl-3 pr-8 text-[12.5px] font-semibold text-ink"
                >
                  <option value="customer">Al cliente</option>
                  <option value="operator">All&apos;operatore</option>
                  <option value="all">A entrambi</option>
                </select>
              )}
              {/* Audit 2026-05-20 (Davide): traduce la bozza nella lingua
                del cliente (auto-rilevata dai messaggi inbound del
                thread). Badge accanto al bottone mostra la lingua di
                destinazione effettivamente usata dopo la traduzione. */}
              <BottoneIcona
                etichetta="Chiedi a Claude"
                icona={<IconaClaude />}
                onClick={() => setAgenteAperto((aperto) => !aperto)}
              />
              <BottoneIcona
                etichetta="Traduci la bozza nella lingua del cliente"
                icona={<IconaMondo />}
                occupato={isTranslatingDraft}
                disabilitato={!body.trim()}
                onClick={() => void translateDraftForCustomer()}
              />
              {lastDraftTargetLang && !isTranslatingDraft && (
                <span
                  className="inline-flex items-center rounded-full bg-green-soft px-2.5 py-0.5 text-[12px] font-semibold text-ink"
                  title="Lingua di destinazione applicata all'ultima traduzione bozza"
                >
                  tradotto in: {lastDraftTargetLang}
                </span>
              )}
              <BozzaStato stato={statoBozza} />
            </div>
            <button
              type="button"
              onClick={() => void submitReply()}
              // Audit 2026-05-29: bloccato anche durante l'upload (l'allegato
              // dev'essere salvato prima dell'invio), ma il testo resta "Invia"
              // — il feedback "Carico…" e' sul bottone Allega.
              disabled={!body.trim() || isSending || isUploading}
              // `shrink-0` + `ml-auto`: l'azione primaria non si comprime e sta
              // sempre in fondo alla barra, anche quando le altre vanno a capo.
              // L'icona e' `aria-hidden`, quindi il nome accessibile resta
              // esattamente "Invia" (i test lo cercano con /^Invia$/).
              className="ml-auto flex h-10 shrink-0 items-center gap-2 rounded-full border border-ink bg-ink px-5 text-[13.5px] font-semibold text-on-ink transition-colors hover:bg-ink-2 disabled:cursor-not-allowed disabled:opacity-45"
            >
              <IconaAereo />
              {isSending ? "Invio..." : "Invia"}
            </button>
          </div>
        </footer>
      )}
    </section>
  );
}
